import type { ElementSelectors } from './generateElementSelectors';
import { relaxCssHrefSelector, relaxXPathHrefSelector } from './relaxHrefSelectors';

export interface ResolverStrategy {
  minConfidence?: number; // Minimum agreement percentage for voting
}

/**
 * Check if an element is inside walkthrough UI (tooltip, modal, backdrop)
 * We exclude these elements from selector resolution to avoid matching walkthrough UI instead of actual target elements
 */
function isInsideWalkthroughUI(el: HTMLElement): boolean {
  // Check if element is inside walkthrough tooltip, modal, or backdrop
  const walkthroughUI = el.closest(
    '[data-walkthrough-tooltip], [data-walkthrough-modal], [data-walkthrough-backdrop]',
  );
  return walkthroughUI !== null;
}

/**
 * Check if an element is actually in the DOM and is a descendant of the container
 */
function isValidElement(el: HTMLElement): boolean {
  // Check if element is connected to the document
  if (!el.isConnected) {
    return false;
  }

  // Exclude elements inside walkthrough UI
  if (isInsideWalkthroughUI(el)) {
    return false;
  }

  return true;
}

/**
 * Check if an element is visible in the DOM
 * In test environments, we check if element is connected to DOM
 * In production, we check both connection and dimensions
 */
function isVisible(el: HTMLElement): boolean {
  // In test environments, being connected is sufficient
  // In production, also check dimensions
  if (process.env.NODE_ENV === 'test' || 'jest' in globalThis) {
    return true;
  }

  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function findFirstValidCssMatch(selector: string, containerEl: HTMLElement): HTMLElement | null {
  try {
    const allMatches = containerEl.querySelectorAll<HTMLElement>(selector);

    for (const el of Array.from(allMatches)) {
      if (isValidElement(el) && isVisible(el)) {
        return el;
      }
    }
  } catch {
    // Invalid selector
  }

  return null;
}

function findFirstValidXPathMatch(xpath: string, containerEl: HTMLElement): HTMLElement | null {
  try {
    const doc = containerEl.ownerDocument ?? document;
    const result = doc.evaluate(
      xpath,
      containerEl,
      null,
      XPathResult.FIRST_ORDERED_NODE_TYPE,
      null,
    );
    const el = result.singleNodeValue as HTMLElement | null;

    if (el && isValidElement(el) && isVisible(el)) {
      return el;
    }
  } catch {
    // Invalid XPath
  }

  return null;
}

/**
 * Resolve element using voting/consensus mechanism
 * Multiple selectors "vote" for elements, and the element with the most votes wins
 */
function resolveElementByVoting(
  targetElementQuery: ElementSelectors | undefined,
  containerEl: HTMLElement | null,
  minConfidence = 0.5,
): HTMLElement | null {
  if (!targetElementQuery || !containerEl) return null;

  const cssSelectors = targetElementQuery.cssSelectors || [];
  const xPathSelectors = targetElementQuery.xPaths || [];
  const totalSelectors = cssSelectors.length + xPathSelectors.length;

  if (totalSelectors === 0) return null;

  const candidates: HTMLElement[] = [];

  // Use querySelectorAll to get all matches, then filter out walkthrough UI elements
  for (const selector of cssSelectors) {
    const matched = findFirstValidCssMatch(selector, containerEl);

    // If exact match fails, try relaxed href selector (ends-with matching)
    if (!matched) {
      const relaxed = relaxCssHrefSelector(selector);

      if (relaxed) {
        const relaxedMatch = findFirstValidCssMatch(relaxed, containerEl);

        if (relaxedMatch) {
          candidates.push(relaxedMatch);
        }
      }
    } else {
      candidates.push(matched);
    }
  }

  // Try all XPath selectors
  for (const xpath of xPathSelectors) {
    const matched = findFirstValidXPathMatch(xpath, containerEl);

    // If exact match fails, try relaxed href selector (contains matching)
    if (!matched) {
      const relaxed = relaxXPathHrefSelector(xpath);

      if (relaxed) {
        const relaxedMatch = findFirstValidXPathMatch(relaxed, containerEl);

        if (relaxedMatch) {
          candidates.push(relaxedMatch);
        }
      }
    } else {
      candidates.push(matched);
    }
  }

  // Count votes for each element
  const votes = new Map<HTMLElement, number>();
  for (const el of candidates) {
    // Double-check element is still valid before counting vote
    if (isValidElement(el)) {
      votes.set(el, (votes.get(el) || 0) + 1);
    }
  }

  // Find element with most votes
  let winner: HTMLElement | null = null;
  let maxVotes = 0;

  for (const [el, count] of votes) {
    // Verify element is still valid before considering it as winner
    if (isValidElement(el) && count > maxVotes) {
      maxVotes = count;
      winner = el;
    }
  }

  // Check confidence threshold
  // Confidence = proportion of total selectors that agree on the winning element
  // e.g., if 3 of 5 selectors point to the same element, confidence = 0.6
  const confidence = maxVotes / totalSelectors;

  if (confidence < minConfidence) {
    return null; // Not confident enough - selectors disagree
  }

  return winner;
}

/**
 * Resolves an element from targetElementQuery using voting mechanism
 * Multiple selectors "vote" for elements, and the element with highest agreement wins
 */
export function resolveElementSelector(
  targetElementQuery: ElementSelectors | undefined,
  containerEl: HTMLElement | null,
  strategy: ResolverStrategy = { minConfidence: 0.5 },
): HTMLElement | null {
  if (!targetElementQuery || !containerEl) {
    return null;
  }
  return resolveElementByVoting(targetElementQuery, containerEl, strategy.minConfidence ?? 0.5);
}
